import { Currency } from '@/modules/common';
import { QuickBooksWarning } from '@/modules/finance/common';

import { QuickBooksBillProjectWarningTypeDTO } from './dto/response/quick-books-bill-project-warning.response';
import { QuickBooksMemberWarningTypeDTO } from './dto/response/quick-books-member-warning.response';

export enum BillHoursSourceType {
  TRACKED = 'TRACKED',
  BOOKED = 'BOOKED',
  CAPACITY = 'CAPACITY',
  TIME_OFF = 'TIME_OFF',
  APPROVED = 'APPROVED',
}

export enum GenerateBillSubRowType {
  PROJECT = 'PROJECT',
  ADDED_PROJECT = 'ADDED_PROJECT',
  TIME_OFF = 'TIME_OFF',
  OVERHEAD = 'OVERHEAD',
  ADD_OVERHEAD = 'ADD_OVERHEAD',
  SALARY_ADJUSTMENT = 'SALARY_ADJUSTMENT',
}

/**
 * Which time-off requests a time-off line is calculated from. Kept separate from
 * `BillHoursSourceType` because its `APPROVED` member means "approved worklog hours" on a
 * project, which is a different concept from an approved time-off request.
 */
export enum BillTimeOffSourceType {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  APPROVED_AND_PENDING = 'APPROVED_AND_PENDING',
}

export interface BillTimeOffSource {
  sourceType: BillTimeOffSourceType;
  hours: number;
  averageRate: number;
  amount: number;
}

export interface BillHoursSource {
  sourceType: BillHoursSourceType;
  hours: number;
  amount: number;
  currencyCode: string;
  averageRate: number;
}

interface GenerateBillBase {
  id: string;
  hours: number;
  effectiveRate: number;
  currency: Currency;
  total: number;
}

export interface GenerateBillMemberProjectListItem extends GenerateBillBase {
  type: GenerateBillSubRowType.PROJECT;
  name: string;
  clientName: string | null;
  logoUrl: string;
  colorHex: string;
  billBy: BillHoursSourceType;
  hoursSources: BillHoursSource[];
  warnings: QuickBooksWarning<QuickBooksBillProjectWarningTypeDTO>[];
}

export interface GenerateBillMemberTimeOffListItem extends GenerateBillBase {
  type: GenerateBillSubRowType.TIME_OFF;
  timeOffTypeId: number;
  timeOffTypeName: string;
  isBillable: boolean;
  billBy: BillTimeOffSourceType;
  hoursSources: BillTimeOffSource[];
}

export interface GenerateBillMemberOverheadListItem {
  type: GenerateBillSubRowType.OVERHEAD;
  id: string;
  description: string;
  effectiveRate: number;
  currency: Currency;
}

export interface GenerateBillAddedProjectListItem {
  type: GenerateBillSubRowType.ADDED_PROJECT;
  id: string;
  projectId: number | null;
  projectName: string;
  logoUrl: string;
  colorHex: string;
  hours: number;
  effectiveRate: number;
  currency: Currency;
  total: number;
}

export interface GenerateBillAddOverheadButtonRow {
  type: GenerateBillSubRowType.ADD_OVERHEAD;
  memberId: string;
  currencyCode: string;
}

/**
 * Auto-generated reconciliation line shown when a member is billed as monthly salary.
 *
 * Its amount is always derived (`monthlySalaryAmount − sum of selected billable lines`),
 * never user-editable, so that the member's bill total equals the agreed salary regardless
 * of which projects / time-offs are selected or how their hours and rates were edited.
 * It is negative when the selected lines already exceed the salary.
 */
export interface GenerateBillSalaryAdjustmentListItem {
  type: GenerateBillSubRowType.SALARY_ADJUSTMENT;
  id: string;
  /** The member's agreed monthly salary — the target the adjustment reconciles towards. */
  monthlySalaryAmount: number;
  /**
   * The reconciling amount actually billed on this line (`salary − selected reconciled lines`).
   * Negative when the selected lines already exceed the salary.
   *
   * Kept distinct from `monthlySalaryAmount`: the two coincide only when nothing else is
   * selected, and conflating them silently bills the wrong figure in every other case.
   */
  adjustmentAmount: number;
  currency: Currency;
}

export type GenerateBillSubRow =
  | GenerateBillMemberProjectListItem
  | GenerateBillAddedProjectListItem
  | GenerateBillMemberTimeOffListItem
  | GenerateBillMemberOverheadListItem
  | GenerateBillSalaryAdjustmentListItem
  | GenerateBillAddOverheadButtonRow;

export interface GenerateBillListItem extends GenerateBillBase {
  member: {
    id: number;
    name: string;
    email: string | null;
    avatarUrl: string | null;
  };
  currencyCode: string;
  warnings: QuickBooksWarning<QuickBooksMemberWarningTypeDTO>[];
  memberProjects: GenerateBillMemberProjectListItem[];
  memberAddedProjects: GenerateBillAddedProjectListItem[];
  memberTimeOffs: GenerateBillMemberTimeOffListItem[];
  memberOverheads: GenerateBillMemberOverheadListItem[];
  memberSubRows: GenerateBillSubRow[];
  /** Server-side eligibility: the billing model was Monthly Salary for the whole period. */
  canSelectMonthlySalary: boolean;
  /** Agreed monthly salary for the period; 0 when not eligible. */
  monthlySalaryAmount: number;
  /**
   * Whether the user has turned monthly-salary billing ON for this member. Page-level UI
   * state (not from the server) — composed onto the row in the page's `tableData` memo.
   */
  isMonthlySalaryEnabled: boolean;
  /** The derived salary-adjustment line, present only while monthly salary is enabled. */
  memberSalaryAdjustment: GenerateBillSalaryAdjustmentListItem | null;
}
